import React, { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { History, Archive, Wrench } from "lucide-react";

const SUB = [
  { key: "activity", label: "Activity Log" },
  { key: "stock", label: "Stock Adjustments" },
  { key: "archived-orders", label: "Archived Orders" },
  { key: "archived-invoices", label: "Archived Invoices" },
];

export default function Audit() {
  const [sub, setSub] = useState("activity");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const url = {
      activity: "/activity-log",
      stock: "/stock-adjustments",
      "archived-orders": "/archive/orders",
      "archived-invoices": "/archive/invoices",
    }[sub];
    api.get(url).then(({ data }) => setRows(data));
  }, [sub]);

  return (
    <div className="space-y-6" data-testid="audit-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Governance</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Audit &amp; Archive</h1>
          <p className="text-sm text-zinc-500 mt-2">Full activity trail, stock adjustments and soft-deleted records.</p>
        </div>
      </div>

      <div className="flex gap-px bg-zinc-200 border border-zinc-200">
        {SUB.map((s) => (
          <button key={s.key} data-testid={`audit-tab-${s.key}`} onClick={() => setSub(s.key)} className={`px-4 py-2.5 text-xs uppercase tracking-wider ${sub === s.key ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}>{s.label}</button>
        ))}
      </div>

      <div className="brutalist-card overflow-x-auto">
        {sub === "activity" && (
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3">When</th><th className="text-left px-5 py-3">User</th>
              <th className="text-left px-5 py-3">Role</th><th className="text-left px-5 py-3">Action</th>
              <th className="text-left px-5 py-3">Entity</th><th className="text-left px-5 py-3">Details</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500"><History className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No activity yet.</td></tr>}
              {rows.map((a) => (
                <tr key={a.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500">{fmtDate(a.at)}</td>
                  <td className="px-5 py-2 text-zinc-950">{a.user_name}</td>
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500">{a.user_role}</td>
                  <td className="px-5 py-2 font-mono text-xs text-zinc-950">{a.action}</td>
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500">{a.entity_type}{a.entity_id ? `/${a.entity_id.slice(0,8)}` : ""}</td>
                  <td className="px-5 py-2 font-mono text-[11px] text-zinc-600 max-w-md truncate">{JSON.stringify(a.details)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {sub === "stock" && (
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3">When</th><th className="text-left px-5 py-3">Part #</th>
              <th className="text-right px-5 py-3">Delta</th><th className="text-left px-5 py-3">Note</th>
              <th className="text-left px-5 py-3">By</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-zinc-500"><Wrench className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No adjustments yet.</td></tr>}
              {rows.map((a) => (
                <tr key={a.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500">{fmtDate(a.at)}</td>
                  <td className="px-5 py-2 font-mono text-zinc-950">{a.part_number}</td>
                  <td className={`px-5 py-2 text-right font-mono ${a.delta < 0 ? "text-rose-600" : "text-emerald-700"}`}>{a.delta > 0 ? "+" : ""}{a.delta}</td>
                  <td className="px-5 py-2 text-xs text-zinc-500">{a.note || "—"}</td>
                  <td className="px-5 py-2 text-zinc-700">{a.by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {sub === "archived-orders" && (
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3">Deleted</th><th className="text-left px-5 py-3">Placed by</th>
              <th className="text-right px-5 py-3">Total</th><th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Deleted by</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-zinc-500"><Archive className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No archived orders.</td></tr>}
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500">{fmtDate(o.deleted_at)}</td>
                  <td className="px-5 py-2 text-zinc-700">{o.created_by_name}</td>
                  <td className="px-5 py-2 text-right font-mono">{fmtCurrency(o.total)}</td>
                  <td className="px-5 py-2 text-xs">{o.status}</td>
                  <td className="px-5 py-2 text-xs text-zinc-500">{o.deleted_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {sub === "archived-invoices" && (
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3">Deleted</th><th className="text-left px-5 py-3">Inv #</th>
              <th className="text-left px-5 py-3">Customer</th><th className="text-right px-5 py-3">Total</th>
              <th className="text-left px-5 py-3">Deleted by</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-zinc-500"><Archive className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No archived invoices.</td></tr>}
              {rows.map((i) => (
                <tr key={i.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                  <td className="px-5 py-2 font-mono text-xs text-zinc-500">{fmtDate(i.deleted_at)}</td>
                  <td className="px-5 py-2 font-mono text-zinc-950">{i.number}</td>
                  <td className="px-5 py-2 text-zinc-700">{i.customer_name}</td>
                  <td className="px-5 py-2 text-right font-mono">{fmtCurrency(i.total)}</td>
                  <td className="px-5 py-2 text-xs text-zinc-500">{i.deleted_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
